import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, Users, TrendingUp } from 'lucide-react'
import { ImageGallery } from '../../common/ImageGallery'

export const BaresEngagementSection: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [currentImages, setCurrentImages] = useState<typeof desktopImages>([])
  const [index, setIndex] = useState(0)

  const desktopImages = [
    { src: '/images/bares/14a-quiz-init.png', alt: 'Quiz - Tela inicial', title: 'Quiz - Tela inicial', description: 'Chame atenção com perguntas rápidas.' },
    { src: '/images/bares/14b-quiz-participate.png', alt: 'Quiz - Participação', title: 'Quiz - Participação', description: 'Clientes participam pelo celular.' },
    { src: '/images/bares/14c-quiz-tv.png', alt: 'Quiz - TV', title: 'Quiz - Exibição na TV', description: 'Mostre ranking em TV ou totem.' }
  ]

  const mobileImage = [
    { src: '/images/bares/14-quiz-cliente-mobile.png', alt: 'Quiz - Mobile', title: 'Quiz - Mobile', description: 'Experiência mobile simples e envolvente.' }
  ]

  const openDesktopGallery = (idx: number) => {
    setCurrentImages(desktopImages)
    setIndex(idx)
    setOpen(true)
  }

  const openMobileGallery = () => {
    setCurrentImages(mobileImage)
    setIndex(0)
    setOpen(true)
  }

  return (
    <section className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-300/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <Sparkles className="w-4 h-4" />
            Recurso Opcional
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Engajamento do Cliente
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Atraia a atenção dos clientes com perguntas rápidas e mostre o ranking no salão. Ideal para promoções, lançamentos de pratos e noites temáticas.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {desktopImages.map((img, i) => (
            <motion.button
              key={img.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-2xl border-2 border-gray-200 p-4 shadow-lg hover:shadow-2xl hover:border-amber-300 transition-all duration-300 hover:-translate-y-2"
              onClick={() => openDesktopGallery(i)}
            >
              <img src={img.src} alt={img.alt} className="w-full rounded-xl border border-gray-100" />
              <div className="mt-3 font-semibold text-gray-700 group-hover:text-amber-600 transition-colors">{img.title}</div>
            </motion.button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6 shadow-lg"
          >
            <div className="flex items-center gap-2 text-amber-700 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <strong className="text-lg">Vantagens</strong>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span>Aumenta o tempo de permanência</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span>Ajuda a divulgar novidades</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-600 rounded-full mt-2"></div>
                <span>Gera dados de preferência</span>
              </li>
            </ul>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="group bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg md:col-span-2 hover:shadow-2xl hover:border-orange-300 transition-all duration-300"
            onClick={openMobileGallery}
          >
            <div className="flex items-center gap-2 text-orange-700 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <strong className="text-lg">Experiência no celular</strong>
            </div>
            <div className="flex justify-center">
              <img src="/images/bares/14-quiz-cliente-mobile.png" alt="Quiz - Experiência mobile" className="h-96 w-auto rounded-xl border-2 border-gray-100 group-hover:scale-[1.02] transition-transform duration-300" />
            </div>
          </motion.button>
        </div>
        <ImageGallery images={currentImages} isOpen={open} onClose={() => setOpen(false)} initialIndex={index} />
      </div>
    </section>
  )
}
